import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { PelicanLogo } from '../components/PelicanLogo';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Heart, Bookmark, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Profile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [uploadedResources, setUploadedResources] = useState([]);
  const [savedResources, setSavedResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();

  const isOwnProfile = currentUser && currentUser.id === userId;

  useEffect(() => {
    fetchProfile();
    fetchUploadedResources();
    if (isOwnProfile) {
      fetchSavedResources();
    }
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}`, { withCredentials: true });
      setProfile(data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadedResources = async () => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}/resources`, { withCredentials: true });
      setUploadedResources(data);
    } catch (error) {
      console.error('Failed to load uploaded resources');
    }
  };

  const fetchSavedResources = async () => {
    try {
      const { data } = await axios.get(`${API}/users/${userId}/saved`, { withCredentials: true });
      setSavedResources(data);
    } catch (error) {
      console.error('Failed to load saved resources');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20">
          <h2 className="text-2xl font-semibold mb-4">User not found</h2>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const ResourceCard = ({ resource }) => (
    <Link to={`/resources/${resource.id}`}>
      <div className="border rounded-lg p-6 hover:-translate-y-0.5 transition-all duration-200 hover:shadow-sm bg-surface" data-testid={`profile-resource-${resource.id}`}>
        <div className="flex items-start justify-between mb-3">
          <span className="text-xs uppercase tracking-[0.1em] font-mono text-primary">{resource.subject}</span>
          <FileText className="h-5 w-5 text-muted" strokeWidth={1.5} />
        </div>
        <h3 className="text-lg font-semibold mb-2 hover:text-primary transition-colors">{resource.title}</h3>
        {resource.description && (
          <p className="text-sm text-muted mb-4 line-clamp-2 leading-relaxed">{resource.description}</p>
        )}
        <div className="flex items-center gap-4 text-sm text-muted">
          <span className="flex items-center gap-1">
            <Heart className="h-4 w-4" strokeWidth={1.5} />
            {resource.likes_count}
          </span>
          <span className="flex items-center gap-1">
            <Bookmark className="h-4 w-4" strokeWidth={1.5} />
            {resource.saves_count}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" strokeWidth={1.5} />
            {formatDistanceToNow(new Date(resource.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="relative h-48 rounded-lg overflow-hidden mb-6 bg-gradient-to-br from-teal-500 via-emerald-400 to-cyan-500 dark:from-teal-900 dark:via-emerald-800 dark:to-cyan-900">
            <div className="absolute inset-0 opacity-20 flex items-center justify-center">
              <PelicanLogo className="h-40 w-40 text-white" strokeWidth={1.5} />
            </div>
          </div>

          <div className="flex items-start gap-6 -mt-20 relative">
            <div className="w-32 h-32 rounded-full bg-primary/10 border-4 border-background flex items-center justify-center text-4xl font-semibold" data-testid="profile-avatar">
              {profile.name[0]}
            </div>
            <div className="flex-1 mt-16">
              <h1 className="text-3xl font-semibold tracking-tight mb-2" data-testid="profile-name">{profile.name}</h1>
              <p className="text-muted mb-2">{profile.email}</p>
              {profile.bio && <p className="text-muted leading-relaxed">{profile.bio}</p>}
              {profile.role === 'admin' && (
                <span className="inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary" data-testid="profile-admin-badge">
                  Admin
                </span>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="uploaded" className="w-full">
          <TabsList className="mb-8" data-testid="profile-tabs">
            <TabsTrigger value="uploaded" data-testid="profile-tab-uploaded">
              Uploaded Notes ({uploadedResources.length})
            </TabsTrigger>
            {isOwnProfile && (
              <TabsTrigger value="saved" data-testid="profile-tab-saved">
                Saved Notes ({savedResources.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="uploaded" data-testid="profile-uploaded-content">
            {uploadedResources.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-accent flex items-center justify-center mb-6">
                  <FileText className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold mb-2">No uploads yet</h3>
                <p className="text-foreground/70 mb-6">
                  {isOwnProfile ? "You haven't uploaded any resources yet" : "This user hasn't uploaded anything yet"}
                </p>
                {isOwnProfile && (
                  <Link to="/upload">
                    <Button data-testid="profile-upload-first">Upload First Resource</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {uploadedResources.map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
              </div>
            )}
          </TabsContent>

          {isOwnProfile && (
            <TabsContent value="saved" data-testid="profile-saved-content">
              {savedResources.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto rounded-full bg-accent flex items-center justify-center mb-6">
                    <Bookmark className="h-10 w-10 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">No saved resources</h3>
                  <p className="text-foreground/70 mb-6">Resources you save will appear here</p>
                  <Link to="/resources">
                    <Button data-testid="profile-browse-resources">Browse Resources</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {savedResources.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};